import { observer } from "mobx-react";
import { TITLE_MAX_CHARS, useDataFetcher } from "./common";
import TabTable, { CommonTabProps } from "./TabTable";
import paths from "~/paths";
import { useEffect, useMemo, useState } from "react";
import bookmarkStore from "~/stores/bookmarks";
import { ColumnDef, getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import TabContent from "./TabContent";
import documentStore, { DocumentId } from "~/stores/document";
import keyBy from "lodash/keyBy";
import moment from "moment";
import { DOC_PUB_DATE_FORMAT } from "~/document";

const BookmarksTab = observer((props: CommonTabProps) => {
  const fetcher = useDataFetcher(props.selected, bookmarkStore.f.loadBare, { clientOnlyPagination: true });
  const [docs, setDocs] = useState<Record<DocumentId, Document>>({});
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    (async () => {
      if (fetcher.items.results.length && props.selected) {
        setLoadingDetails(true);
        const ids = fetcher.items.results.map((a) => a.document);
        const docs = await documentStore.loadIds(ids);
        setDocs(keyBy(Object.values(docs), "id") as any);
        setLoadingDetails(false);
      }
    })();
  }, [fetcher.items.results, props.selected]);

  const bookmarks = useMemo(
    () =>
      fetcher.items.results.map((bookmark) => {
        let docId = bookmark.document;
        const doc = docs[docId];
        return { ...bookmark, document_data: doc };
      }),
    [fetcher.items.results, docs]
  );

  const data = useMemo(() => bookmarks as any, [bookmarks, docs]);
  const formatDate = (v: any) => (v ? moment(v).format(DOC_PUB_DATE_FORMAT) : "N/A");
  const columns: ColumnDef<any, any>[] = useMemo(
    () => [
      {
        id: "title",
        header: "Document",
        accessorKey: "title",
        cell: (info) => {
          const doc = info.row.original;
          let title = doc.title;
          title = title.length > TITLE_MAX_CHARS ? `${title.substr(0, TITLE_MAX_CHARS)}...` : title;
          return (
            <div>
              {doc.document_data?.protocol ? (
                <Link to={paths.construct.docDetail(doc.document_data?.protocol)}>{title}</Link>
              ) : (
                <>{title}</>
              )}
            </div>
          );
        },
      },
      {
        id: "publication_date",
        header: "Publication date",
        accessorFn: (row) => row.document_data?.publication_date,
        cell: (info) => formatDate(info.getValue()),
      },
      {
        id: "release_date",
        header: "Release date",
        accessorFn: (row) => row.document_data?.release_date,
        cell: (info) => formatDate(info.getValue()),
      },
      {
        id: "expiration_date",
        header: "Expiration date",
        accessorFn: (row) => row.document_data?.expiration_date,
        cell: (info) => formatDate(info.getValue()),
      },
      {
        id: "owner_display",
        header: "Documents manager",
        accessorFn: (row) => row.document_data?.owner_display,
        cell: (info) => info.getValue() || "N/A",
      },
      {
        id: "last_modified_by",
        header: "Last modified by",
        accessorFn: (row) => row.document_data?.last_modified_user?.email,
      },
      {
        id: "office_display",
        header: "Office division",
        accessorFn: (row) => row.document_data?.office_display,
        cell: (info) => info.getValue() || "N/A",
      },
      {
        id: "protocol",
        header: "Document ID",
        accessorFn: (row) => row.document_data?.protocol,
      },
    ],
    [bookmarks, docs]
  );

  const tableInstance = useReactTable({
    data,
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  return (
    <TabContent>
      <TabTable
        fetcher={fetcher}
        tableInstance={tableInstance}
        loading={loadingDetails}
        emptyTitle="No bookmarks"
        emptyMessage="You don't have any documents bookmarked"
      />
    </TabContent>
  );
});

export default BookmarksTab;
